import { Avatar, Button, Popover, Stack, Typography } from "@mui/material";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, LocalizationProvider, type PickerValidDate } from "@mui/x-date-pickers";
import React from "react";
import dayjs from "dayjs";
import de from 'dayjs/locale/de';
import type { PickerValue } from "@mui/x-date-pickers/internals";
import type { PickerSelectionState } from "@mui/x-date-pickers/internals/hooks/usePicker";
import { useText } from "../../main.tsx";

type AppointmentCallProps = {
    currentISODateString?: string;
    onSubmit: (timestamp: string) => void;
};

const AppointmentCall: React.FC<AppointmentCallProps> = ({ currentISODateString, onSubmit }) => {
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const minDate = dayjs().add(2, 'w');
    const maxDate = minDate.add(1, 'y');
    const initial = currentISODateString ? dayjs(currentISODateString) : null;
    const [pickedDate, setPickedDate] = React.useState<dayjs.Dayjs | null>(
        initial && initial.isAfter(minDate) ? initial : null
    );

    const { t } = useText();

    dayjs.locale(de);

    const handleDateChange = (date: PickerValue, selectionDate?: PickerSelectionState) => {
        if (selectionDate === 'finish') setOpen(false);
        if (!date) {
            return;
        }

        date = dayjs(date);

        setPickedDate(date);
        onSubmit(date.toISOString());
    }

    return (
        <Stack spacing={1}>
            <Typography variant={"h2"}>
                {t('funnel.wizard.appointmentSelection.title')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ width: 43, height: 43, backgroundColor: "var(--mui-palette-info-main)" }}>
                    <CalendarMonth fontSize={"small"} htmlColor={"var(--mui-palette-background-default)"} />
                </Avatar>
                <Button
                    variant="text"
                    sx={{ textDecoration: "underline" }}
                    onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                        setOpen(true);
                    }}
                >
                    {pickedDate && pickedDate.isValid() ? pickedDate.format('DD.MM.YYYY') : t('funnel.wizard.appointmentSelection.buttonText')}
                </Button>
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setOpen(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"de"}>
                        <DateCalendar
                            shouldDisableDate={(day: PickerValidDate) => [0, 6].indexOf(day.day()) !== -1}
                            value={pickedDate}
                            onChange={handleDateChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            dayOfWeekFormatter={(weekday) => `${weekday.format('dd')}`}
                        />
                    </LocalizationProvider>
                </Popover>
            </Stack>
        </Stack>
    );
};

export default AppointmentCall;
